import React, { useState } from "react";
import { Bot, Eye, FileDown, Globe, RotateCcw, Send, Tag, User, X } from "lucide-react";
import { formatEuro } from "../../utils/offerCalc";

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// clientShare (présentations et rapports d'un projet) : version partagée sur le portail client, et partage en un clic.
const RevisionsPanel = ({ revisions, canEdit, viewingId, onView, onRestore, onLabel, onClose, clientShare = null }) => {
  const [labelling, setLabelling] = useState(null);
  const [label, setLabel] = useState("");

  return (
    <aside className="w-full shrink-0 space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:w-80">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Versions</h2>
        <button type="button" onClick={onClose} aria-label="Fermer les versions" className="rounded p-1 text-slate-400 hover:bg-slate-100">
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Chaque enregistrement crée une version. Restaurer en crée une nouvelle : rien n'est jamais effacé.
      </p>
      {clientShare && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          {clientShare.share
            ? "Le client voit la version marquée « Client » sur son portail, pas vos modifications en cours."
            : "Aucune version n'est partagée avec le client pour l'instant."}
        </p>
      )}
      <ol className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
        {revisions.map((revision) => (
          <li
            key={revision.id}
            className={`rounded-lg border p-3 text-sm ${
              viewingId === revision.id ? "border-blue-400 bg-blue-50" : revision.is_current ? "border-slate-300" : "border-slate-200"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-medium text-slate-900">
                  v{revision.number}
                  {revision.is_current && <span className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] text-white">actuelle</span>}
                  {revision.client_share && (
                    <span title={`Partagée avec le client le ${formatDate(revision.client_share.published_at)}${revision.client_share.published_by_name ? ` par ${revision.client_share.published_by_name}` : ""}`}
                      className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] text-white">
                      <Globe className="h-3 w-3" /> Client
                    </span>
                  )}
                  {revision.exported_at && (
                    <span title={`PDF exporté le ${formatDate(revision.exported_at)} : cette version reste telle qu'elle est partie`}
                      className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                      <FileDown className="h-3 w-3" /> PDF
                    </span>
                  )}
                </div>
                {revision.label && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    <Tag className="h-3 w-3" /> {revision.label}
                  </div>
                )}
              </div>
              {revision.total_net !== undefined && (
                <div className="text-right text-xs tabular-nums">
                  <div className="font-semibold text-blue-700">{formatEuro(revision.total_net)} HT</div>
                  {Math.abs((revision.total_gross || 0) - (revision.total_net || 0)) >= 0.01 && (
                    <div className="text-slate-400">{formatEuro(revision.total_gross)} avant remise</div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
              {revision.source === "agent" ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
              {revision.author_name || "—"} · {formatDate(revision.updated_at)}
            </div>
            {revision.summary && <div className="mt-1 text-xs text-slate-600">{revision.summary}</div>}
            {labelling === revision.id ? (
              <form
                className="mt-2 flex gap-1"
                onSubmit={(event) => {
                  event.preventDefault();
                  onLabel(revision.id, label.trim());
                  setLabelling(null);
                }}
              >
                <input
                  autoFocus
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="Ex. v1 envoyée"
                  aria-label="Nom de la version"
                  className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                />
                <button type="submit" className="rounded bg-slate-900 px-2 py-1 text-xs text-white">OK</button>
              </form>
            ) : (
              <div className="mt-2 flex gap-1">
                {!revision.is_current && (
                  <button type="button" onClick={() => onView(revision.id)}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">
                    <Eye className="h-3 w-3" /> Voir
                  </button>
                )}
                {canEdit && !revision.is_current && (
                  <button type="button" onClick={() => onRestore(revision.id)}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">
                    <RotateCcw className="h-3 w-3" /> Restaurer
                  </button>
                )}
                {clientShare?.canShare && (revision.client_share ? (
                  <button type="button" onClick={clientShare.onWithdraw}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50">
                    <Globe className="h-3 w-3" /> Ne plus partager
                  </button>
                ) : (
                  <button type="button" onClick={() => clientShare.onShare(revision)}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">
                    <Send className="h-3 w-3" /> Partager au client
                  </button>
                ))}
                {canEdit && (
                  <button type="button" onClick={() => { setLabelling(revision.id); setLabel(revision.label || ""); }}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">
                    <Tag className="h-3 w-3" /> Nommer
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </aside>
  );
};

export default RevisionsPanel;
